import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { translateTextOptimized } from "../utils/OptimizedTranslationAPI";
import RecipeComments from "../components/recipe/RecipeComments.jsx";
import favoriteService from "../services/FavoriteService.js";

/**
 * Page pour afficher les détails d'une recette
 * Page complète avec commentaires intégrés
 * Traduction automatique des ingrédients et instructions
 */
const RecipeDetailsPage = () => {
  const { t, language, translateIngredient, translateInstructions } =
    useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);

  // États pour la traduction automatique
  const [translatedIngredients, setTranslatedIngredients] = useState([]);
  const [translatedInstructions, setTranslatedInstructions] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);

  /**
   * Charger les données de la recette
   */
  useEffect(() => {
    loadRecipeData();
    loadUserData();
  }, [id]);

  // Traduction automatique via API quand la langue change
  useEffect(() => {
    if (!recipe) return;
    const translateContent = async () => {
      // Pas de traduction si langue anglaise
      if (language === "en") {
        setTranslatedIngredients(formatIngredients(recipe.ingredients, false));
        setTranslatedInstructions(
          formatInstructions(recipe.instructions, false),
        );
        return;
      }

      setIsTranslating(true);

      try {
        // TRADUCTION COMPLÈTE AVEC API (Tous les mots - illimité!)
        const ingredientLines =
          recipe.ingredients?.split("\n").filter((line) => line.trim()) || [];

        // Traduire TOUS les ingrédients avec l'API optimisée (dictionnaire massif + APIs)
        const translatedIngList = await Promise.all(
          ingredientLines.map(async (ingredient) => {
            // Utiliser l'API optimisée avec dictionnaire de 1,000,000+ mots
            const optimizedTranslation = await translateTextOptimized(
              ingredient,
              language,
              "auto",
            );
            return optimizedTranslation || ingredient;
          }),
        );

        // Traduire TOUTES les instructions avec l'API optimisée
        const instructionLines =
          recipe.instructions?.split("\n").filter((line) => line.trim()) || [];

        const translatedInstList = await Promise.all(
          instructionLines.map(async (instruction) => {
            // Utiliser l'API optimisée avec dictionnaire de 1,000,000+ mots
            const optimizedTranslation = await translateTextOptimized(
              instruction,
              language,
              "auto",
            );
            return optimizedTranslation || instruction;
          }),
        );

        setTranslatedIngredients(translatedIngList);
        setTranslatedInstructions(translatedInstList);
      } catch (error) {
        console.error("Translation error:", error);
        // Fallback au dictionnaire local
        setTranslatedIngredients(formatIngredients(recipe.ingredients, true));
        setTranslatedInstructions(
          formatInstructions(recipe.instructions, true),
        );
      } finally {
        setIsTranslating(false);
      }
    };

    translateContent();
  }, [language, recipe]);

  const loadRecipeData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:3000/api/recipes/${id}`);

      if (response.ok) {
        const result = await response.json();
        // L'API retourne { success: true, data: recipe }
        const recipeData = result.data || result;

        // 🐛 DÉBOGAGE - Afficher les données reçues
        console.log("📦 Données de la recette reçues :", recipeData);
        console.log("📷 image_url :", recipeData.image_url);
        console.log("🎥 video_url :", recipeData.video_url);
        console.log("🔍 Type de video_url :", typeof recipeData.video_url);

        setRecipe(recipeData);

        // Initialiser les traductions avec les données originales
        if (recipeData) {
          const ingredientLines =
            recipeData.ingredients?.split("\n").filter((line) => line.trim()) ||
            [];
          const instructionLines =
            recipeData.instructions
              ?.split("\n")
              .filter((line) => line.trim()) || [];
          setTranslatedIngredients(ingredientLines);
          setTranslatedInstructions(instructionLines);
        }
      } else {
        setError(t("recipeNotFound"));
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la recette:", error);
      setError(t("errorLoadingRecipe"));
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = () => {
    try {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");

      if (storedUser && storedToken) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        checkFavoriteStatus(userData);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'utilisateur:", error);
    }
  };

  /**
   * Vérifier si la recette est en favoris
   */
  const checkFavoriteStatus = async (userData) => {
    if (!userData || !recipe) return;
    try {
      const status = await favoriteService.checkFavoriteStatus(
        recipe.id,
        userData.id,
      );
      setIsFavorite(status);
    } catch (error) {
      console.error("Erreur vérification favoris:", error);
    }
  };

  /**
   * Gérer les favoris
   */
  const handleToggleFavorite = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    try {
      const newStatus = await favoriteService.toggleFavorite(
        recipe.id,
        user.id,
        isFavorite,
      );
      setIsFavorite(newStatus);
    } catch (error) {
      console.error("Erreur toggle favori:", error);
    }
  };

  /**
   * Gérer le retour
   */
  const handleBack = () => {
    navigate(-1);
  };

  /**
   * Formater les ingrédients
   */
  const formatIngredients = (ingredients, useLocal = false) => {
    if (!ingredients) return [];
    const lines = ingredients.split("\n").filter((line) => line.trim());
    if (useLocal && (language === "km" || language === "fr")) {
      return lines.map((ingredient) => translateIngredient(ingredient));
    }
    return lines;
  };

  /**
   * Formater les instructions
   */
  const formatInstructions = (instructions, useLocal = false) => {
    if (!instructions) return [];
    const lines = instructions.split("\n").filter((line) => line.trim());
    if (useLocal && (language === "km" || language === "fr")) {
      return lines.map((instruction) => translateInstructions(instruction));
    }
    return lines;
  };

  /**
   * Formater le temps
   */
  const formatTime = (minutes) => {
    if (!minutes) return t("notSpecified");
    if (minutes < 60) return `${minutes} ${t("minutes")}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0
      ? `${hours}${t("hours")} ${mins}${t("minutes")}`
      : `${hours}${t("hours")}`;
  };

  /**
   * Obtenir la couleur de difficulté
   */
  const getDifficultyColor = (difficulty) => {
    const colors = {
      [t("easy")]: "bg-green-100 text-green-800 border-green-300",
      [t("medium")]: "bg-yellow-100 text-yellow-800 border-yellow-300",
      [t("hard")]: "bg-red-100 text-red-800 border-red-300",
    };
    return colors[difficulty] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("loadingRecipe")}</p>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t("recipeNotFound")}
          </h2>
          <p className="text-gray-600 mb-6">
            {error || t("recipeDoesNotExist")}
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← {t("back")}
          </button>
        </div>
      </div>
    );
  }

  const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-orange-50 w-full min-w-0 overflow-x-hidden">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10 w-full">
        {/* Bouton de retour */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors mb-4 sm:mb-6 font-medium text-sm sm:text-base"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span>{t("back")}</span>
        </button>

        {/* En-tête de la recette */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden mb-6 sm:mb-10 border-2 sm:border-4 border-purple-200 transform hover:scale-[1.01] transition-transform duration-300">
          {/* Image header */}
          <div className="h-48 sm:h-64 md:h-80 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 relative overflow-hidden">
            {recipe.image_url ? (
              <img
                src={recipe.image_url}
                alt={recipe.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextElementSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ display: recipe.image_url ? "none" : "flex" }}
            >
              <span className="text-9xl drop-shadow-2xl">🍳</span>
            </div>

            {/* Overlay gradient pour meilleure lisibilité */}
            {recipe.image_url && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            )}

            {/* Actions flottantes */}
            <div className="absolute top-4 right-4 flex space-x-3">
              {user && (
                <button
                  onClick={handleToggleFavorite}
                  className={`p-3 rounded-xl backdrop-blur-sm transition-all duration-200 ${
                    isFavorite
                      ? "bg-red-500 text-white shadow-lg"
                      : "bg-white/20 text-white hover:bg-white/30"
                  }`}
                  title={
                    isFavorite ? t("removeFromFavorites") : t("addToFavorites")
                  }
                >
                  {isFavorite ? "❤️" : "🤍"}
                </button>
              )}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="p-10">
            {/* Titre et difficulté */}
            <div className="flex items-start justify-between mb-8">
              <div className="flex-1">
                <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 mb-4 drop-shadow-lg">
                  {recipe.title}
                </h1>
                {recipe.description && (
                  <p className="text-xl text-gray-700 leading-relaxed font-medium">
                    {recipe.description}
                  </p>
                )}
              </div>
              <span
                className={`ml-6 px-6 py-3 rounded-2xl text-lg font-bold border-4 shadow-xl transform hover:scale-110 transition-transform ${getDifficultyColor(
                  recipe.difficulty,
                )}`}
              >
                {recipe.difficulty}
              </span>
            </div>

            {/* Informations rapides */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-3xl p-6 text-center border-4 border-purple-300 shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
                <div className="text-5xl mb-3">⏱️</div>
                <div className="text-2xl font-black text-purple-900">
                  {formatTime(recipe.prep_time)}
                </div>
                <div className="text-base font-bold text-purple-700 mt-1">
                  {t("preparation")}
                </div>
              </div>

              <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-3xl p-6 text-center border-4 border-pink-300 shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
                <div className="text-5xl mb-3">🔥</div>
                <div className="text-2xl font-black text-pink-900">
                  {formatTime(recipe.cook_time)}
                </div>
                <div className="text-base font-bold text-pink-700 mt-1">
                  {t("cooking")}
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-3xl p-6 text-center border-4 border-orange-300 shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
                <div className="text-5xl mb-3">👥</div>
                <div className="text-2xl font-black text-orange-900">
                  {recipe.servings || "N/A"}
                </div>
                <div className="text-base font-bold text-orange-700 mt-1">
                  {t("servings")}
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-3xl p-6 text-center border-4 border-amber-300 shadow-2xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
                <div className="text-5xl mb-3">⏰</div>
                <div className="text-2xl font-black text-amber-900">
                  {formatTime(totalTime)}
                </div>
                <div className="text-base font-bold text-amber-700 mt-1">
                  {t("total")}
                </div>
              </div>
            </div>

            {/* Vidéo (si disponible) */}
            {(() => {
              const hasVideo =
                recipe.video_url &&
                typeof recipe.video_url === "string" &&
                recipe.video_url.trim() !== "";
              console.log("🎬 Vérification affichage vidéo");
              console.log("   video_url existe ?", !!recipe.video_url);
              console.log("   video_url valeur :", recipe.video_url);
              console.log("   Type :", typeof recipe.video_url);
              console.log("   Est une string non vide ?", hasVideo);
              console.log("   ✅ Vidéo s'affichera :", hasVideo);
              return null;
            })()}
            {recipe.video_url &&
              typeof recipe.video_url === "string" &&
              recipe.video_url.trim() !== "" && (
                <div className="mb-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-8 border-4 border-purple-300 shadow-2xl">
                  <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-6 flex items-center">
                    <span className="text-5xl mr-4">🎥</span>
                    {t("video") || "Vidéo de la recette"}
                  </h3>
                  <div
                    className="relative w-full"
                    style={{ paddingBottom: "56.25%" }}
                  >
                    {recipe.video_url.includes("youtube.com") ||
                    recipe.video_url.includes("youtu.be") ? (
                      <iframe
                        className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg"
                        src={(() => {
                          // Extraire l'ID de la vidéo YouTube
                          let videoId = "";
                          if (recipe.video_url.includes("watch?v=")) {
                            videoId = recipe.video_url
                              .split("watch?v=")[1]
                              ?.split("&")[0];
                          } else if (recipe.video_url.includes("youtu.be/")) {
                            videoId = recipe.video_url
                              .split("youtu.be/")[1]
                              ?.split("?")[0];
                          } else if (recipe.video_url.includes("embed/")) {
                            videoId = recipe.video_url
                              .split("embed/")[1]
                              ?.split("?")[0];
                          }
                          return `https://www.youtube.com/embed/${videoId}`;
                        })()}
                        title="Vidéo de la recette"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="absolute top-0 left-0 w-full h-full bg-gray-100 rounded-xl flex items-center justify-center">
                        <a
                          href={recipe.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors flex items-center space-x-2 shadow-lg"
                        >
                          <span>🎬</span>
                          <span>Voir la vidéo</span>
                        </a>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    📺 Lien vidéo :{" "}
                    <a
                      href={recipe.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:underline"
                    >
                      {recipe.video_url}
                    </a>
                  </p>
                </div>
              )}

            {/* Ingrédients et Instructions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
              {/* Ingrédients */}
              <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl p-8 border-4 border-green-300 shadow-2xl transform hover:scale-[1.02] transition-transform duration-300">
                <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 mb-6 flex items-center">
                  <span className="text-5xl mr-4">🥘</span>
                  {t("ingredients")}
                  {isTranslating && (
                    <span className="text-sm text-gray-500 animate-pulse ml-2">
                      ⏳
                    </span>
                  )}
                </h3>
                <ul className="space-y-4">
                  {translatedIngredients
                    .filter((ingredient) => {
                      // Filtrer les lignes vides
                      const trimmed = ingredient.trim();
                      return trimmed.length > 0;
                    })
                    .map((ingredient, index) => {
                      const trimmed = ingredient.trim();

                      // Détecter si c'est un titre de section (commence par emoji ou contient ':')
                      const isTitle =
                        /^[🌶️🧂🦐🌽🧈🍋]/.test(trimmed) ||
                        (trimmed.includes(":") && trimmed.length < 50);

                      if (isTitle) {
                        return (
                          <li
                            key={index}
                            className="text-lg font-black text-green-800 mt-6 mb-3 bg-green-200 px-4 py-2 rounded-xl border-2 border-green-400"
                          >
                            {trimmed}
                          </li>
                        );
                      }

                      // Nettoyer l'ingrédient en retirant les marqueurs au début
                      const cleanedIngredient = trimmed
                        .replace(/^[-•]\s*/, "") // Retire "- " ou "• "
                        .trim();

                      return (
                        <li
                          key={index}
                          className="flex items-start text-gray-800 bg-white px-4 py-3 rounded-xl border-2 border-green-200 hover:shadow-lg transition-shadow"
                        >
                          <span className="text-green-600 mr-4 mt-0.5 text-2xl font-black">
                            •
                          </span>
                          <span className="text-lg font-medium">
                            {cleanedIngredient}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>

              {/* Instructions */}
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl p-8 border-4 border-blue-300 shadow-2xl transform hover:scale-[1.02] transition-transform duration-300">
                <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600 mb-6 flex items-center">
                  <span className="text-5xl mr-4">👨‍🍳</span>
                  {t("instructions")}
                  {isTranslating && (
                    <span className="text-sm text-gray-500 animate-pulse ml-2">
                      ⏳
                    </span>
                  )}
                </h3>
                <ol className="space-y-5">
                  {translatedInstructions
                    .filter((instruction) => {
                      // Filtrer les lignes vides et les lignes qui ne contiennent que des chiffres
                      const trimmed = instruction.trim();
                      return trimmed.length > 0 && !/^\d+\.?$/.test(trimmed);
                    })
                    .map((instruction, index) => {
                      // Nettoyer l'instruction en retirant les numéros au début
                      const cleanedInstruction = instruction
                        .replace(/^\d+[\.\)]\s*/, "") // Retire "1. " ou "1) "
                        .replace(/^[-•]\s*/, "") // Retire "- " ou "• "
                        .trim();

                      return (
                        <li
                          key={index}
                          className="flex items-start text-gray-800 bg-white px-4 py-4 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-shadow"
                        >
                          <span className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-2xl w-12 h-12 flex items-center justify-center text-lg font-black mr-5 shadow-lg flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="text-lg font-medium leading-relaxed">
                            {cleanedInstruction || instruction}
                          </span>
                        </li>
                      );
                    })}
                </ol>
              </div>
            </div>

            {/* Informations sur l'auteur */}
            {recipe.username && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200 mb-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4">
                    {recipe.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {t("createdBy")}{" "}
                      {recipe.first_name && recipe.last_name
                        ? `${recipe.first_name} ${recipe.last_name}`
                        : recipe.username}
                    </div>
                    <div className="text-gray-600 text-sm">
                      {new Date(recipe.created_at).toLocaleDateString("fr-FR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section des commentaires */}
        <RecipeComments
          recipeId={parseInt(id)}
          user={user}
          token={localStorage.getItem("token")}
        />
      </div>
    </div>
  );
};

export default RecipeDetailsPage;
